import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ROLE_GROUPS, TRADE_EVENTS } from '@moal/api'
import { useContext, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { MEMBERS, MODERATORS, TRADES } from '../../../constants/chatRooms'
import * as FEATURES from '../../../constants/features'
import * as SETTINGS from '../../../constants/settings'
import { AuthenticationContext } from '../../../context/authenticationContext'
import { SiteConfigContext } from '../../../context/siteConfigContext'
import { SocketContext } from '../../../context/socketContext'
import { PRODUCT_CODE } from '../../../utils/ENV'
import TradeSearch from '../../Forms/TradeSearch'
import MessageList from '../../Messages/MessageList'
import PinnedMessage from '../../Utilities/PinnedMessage'
import Poll from '../../Utilities/Poll'
import TradeAlert from '../../Utilities/TradeAlert'
import ChatSectionNav from '../ChatSectionNav'

const ChatSection = () => {
  const { user } = useContext(AuthenticationContext)
  const { updateSetting } = useContext(SiteConfigContext)
  const {
    setReplyTo,
    activePoll,
    [SETTINGS.PINNED_MESSAGE.key]: pinnedMessage,
    [SETTINGS.FEATURES.key]: enabledFeatures
  } = useContext(SiteConfigContext)
  const tradesEnabled = enabledFeatures?.[FEATURES.TRADES]
  const publicChatEnabled = enabledFeatures?.[FEATURES.PUBLIC_CHAT]
  const privateChatEnabled = enabledFeatures?.[FEATURES.PRIVATE_CHAT]
  const onlyOneEnabled =
    [tradesEnabled, publicChatEnabled, privateChatEnabled].filter(Boolean)
      .length <= 1

  const { tradesSocket } = useContext(SocketContext)
  const [searchParams, setSearchParams] = useSearchParams()

  const [showAlert, setShowAlert] = useState(false)
  const [tradeData, setTradeData] = useState()
  const [search, setSearch] = useState(null)
  const [showPinnedMessage, setShowPinnedMessage] = useState(true)

  const [channel, setChannel] = useState(() => {
    const paramChannel = searchParams.get('channel')
    if (paramChannel) return paramChannel
    if (publicChatEnabled) return MEMBERS
    if (privateChatEnabled) return MODERATORS
    if (tradesEnabled) return TRADES
    return null
  })

  useEffect(() => {
    if (tradesSocket) {
      tradesSocket.on(TRADE_EVENTS.POST, handlePostTrade)
      tradesSocket.on(TRADE_EVENTS.PUT, handlePostTrade)
    }
    return () => {
      if (tradesSocket) {
        tradesSocket.off(TRADE_EVENTS.POST, handlePostTrade)
        tradesSocket.off(TRADE_EVENTS.PUT, handlePostTrade)
      }
    }
  }, [tradesSocket, channel])

  const isModRole = user && ROLE_GROUPS.STAFF.includes(user?.role?.role)

  const handleSetChannel = (selected) => {
    if (channel !== selected) {
      setChannel(selected)
      setSearchParams({ channel: selected })
      setSearch(null)
    }

    if (selected === TRADES) {
      setShowAlert(false)
    }

    setReplyTo(null)
  }

  const unPinMessage = async () => {
    await updateSetting(SETTINGS.PINNED_MESSAGE, null)
  }

  useEffect(() => {
    setShowPinnedMessage(true)
  }, [pinnedMessage])

  const handleSearch = (value) => {
    setSearch(value)
  }

  const handlePostTrade = (data) => {
    if (data.itemNumber === PRODUCT_CODE) {
      if (channel !== TRADES) {
        setShowAlert(true)
        setTradeData(data)
      }
    }
  }

  if (!publicChatEnabled && !tradesEnabled && !privateChatEnabled) {
    return null
  }

  return (
    <div className="relative w-full md:w-fit h-full flex flex-col overflow-auto md:overflow-visible md:pb-2 md:px-2 md:gap-2 md:mt-2">
      {!onlyOneEnabled && (
        <ChatSectionNav active={channel} handleSetChannel={handleSetChannel} />
      )}
      {showAlert && (
        <div className="h-auto">
          <TradeAlert message={tradeData} toggleAlert={setShowAlert} />
        </div>
      )}
      {!showAlert && activePoll && !activePoll.isArchived && (
        <div className="h-auto shadow">
          <Poll {...activePoll} />
        </div>
      )}
      {!showAlert &&
        !activePoll &&
        showPinnedMessage &&
        pinnedMessage &&
        channel === MEMBERS && (
          <div className="md:max-w-96">
            <PinnedMessage
              message={pinnedMessage}
              unPin={unPinMessage}
              toggle={setShowPinnedMessage}
            />
          </div>
        )}
      {channel === TRADES && (
        <div className="sticky bottom-0 shrink md:max-w-96">
          <TradeSearch handleSearch={handleSearch} />
        </div>
      )}
      {channel === MODERATORS && isModRole ? (
        <ModWarning />
      ) : (
        <MessageList channel={channel} search={search} />
      )}
    </div>
  )
}

const ModWarning = () => {
  return (
    <div
      className={`flex flex-col-reverse grow md:border bg-white dark:bg-black border-neutral-200 dark:border-neutral-900 md:rounded-xl shadow-md pb-2 scrollbar-hide md:w-96 `}
    >
      <div className="w-full">
        <Link to="/mod/private-chat-history" target="_blank" className="link">
          <div
            className={`rounded-xl w-full flex items-center p-2 transition-all shadow bg-red-600 dark:bg-red-900`}
          >
            <FontAwesomeIcon
              className={`mr-2 h-4 text-white dark:text-red-200`}
              icon={faTriangleExclamation}
            />
            <p className={`font-medium text-sm text-white dark:text-red-200`}>
              Staff should use the Private Messages History in the Admin
              Controls.
            </p>
          </div>
        </Link>
      </div>
    </div>
  )
}

export default ChatSection
